import { useRef, useState } from "react";
import { Upload, X, Camera, Image as ImageIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isCameraAvailable, takePhoto, pickFromGallery } from "@/lib/native-camera";

interface SourceSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onTakePhoto: () => void;
  onPickFromGallery: () => void;
}

const SourceSelectionDialog = ({
  open,
  onOpenChange,
  title,
  onTakePhoto,
  onPickFromGallery,
}: SourceSelectionDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      hideCloseButton
      className="w-[min(92vw,320px)] max-w-none rounded-2xl overflow-hidden p-5"
    >
      <DialogHeader className="flex flex-row items-center justify-between space-y-0">
        <DialogTitle className="text-center flex-1">{title}</DialogTitle>
        <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogHeader>
      <div className="flex flex-col gap-3 mt-2">
        <Button
          variant="outline"
          className="w-full h-auto min-h-14 justify-start gap-4 text-base whitespace-normal"
          onClick={onTakePhoto}
        >
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Camera className="h-5 w-5 text-primary" />
          </div>
          <span className="flex-1 text-left">Take Photo</span>
        </Button>
        <Button
          variant="outline"
          className="w-full h-auto min-h-14 justify-start gap-4 text-base whitespace-normal"
          onClick={onPickFromGallery}
        >
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <ImageIcon className="h-5 w-5 text-primary" />
          </div>
          <span className="flex-1 text-left">Choose from Gallery</span>
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

interface ImageUploaderProps {
  image: string | null;
  loading?: boolean;
  onUpload: (file: File) => void;
  onImageDataUrl?: (dataUrl: string, source?: 'camera' | 'gallery') => void;
  onRemove?: () => void;
}

export const ImageUploader = ({
  image,
  loading = false,
  onUpload,
  onImageDataUrl,
  onRemove,
}: ImageUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [capturingImage, setCapturingImage] = useState(false);

  const handleClick = () => {
    // On native platforms, show source selection dialog
    if (isCameraAvailable()) {
      setShowSourceDialog(true);
    } else {
      // On web, use file input directly
      inputRef.current?.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    // Reset file input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleTakePhoto = async () => {
    setShowSourceDialog(false);
    setCapturingImage(true);
    try {
      const dataUrl = await takePhoto();
      if (dataUrl) {
        if (onImageDataUrl) {
          onImageDataUrl(dataUrl, 'camera');
        } else {
          // Fallback: convert to file and use onUpload
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          onUpload(file);
        }
      }
    } catch (error) {
      console.error('Error taking photo:', error);
    } finally {
      setCapturingImage(false);
    }
  };

  const handlePickFromGallery = async () => {
    setShowSourceDialog(false);
    setCapturingImage(true);
    try {
      const dataUrl = await pickFromGallery();
      if (dataUrl) {
        if (onImageDataUrl) {
          onImageDataUrl(dataUrl, 'gallery');
        } else {
          // Fallback: convert to file and use onUpload
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'gallery-photo.jpg', { type: 'image/jpeg' });
          onUpload(file);
        }
      }
    } catch (error) {
      console.error('Error picking from gallery:', error);
    } finally {
      setCapturingImage(false);
    }
  };

  if (loading || capturingImage) {
    return (
      <div className="w-full aspect-[4/3] rounded-xl overflow-hidden">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (image) {
    return (
      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden border border-border">
        <img
          src={image}
          alt="Uploaded room"
          className="w-full h-full object-cover"
        />
        <Button
          variant="destructive"
          size="icon"
          className="absolute top-2 right-2 h-8 w-8 rounded-full"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
        <button
          onClick={handleClick}
          className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100"
        >
          <span className="text-white font-medium bg-black/50 px-4 py-2 rounded-full">
            Change Photo
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="hidden"
        />

        {/* Source Selection Dialog */}
        <SourceSelectionDialog
          open={showSourceDialog}
          onOpenChange={setShowSourceDialog}
          title="Change Photo"
          onTakePhoto={handleTakePhoto}
          onPickFromGallery={handlePickFromGallery}
        />
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-muted-foreground/30 bg-white flex flex-col items-center justify-center gap-3 hover:border-primary/50 hover:bg-muted/50 transition-colors"
      >
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Upload className="h-6 w-6 text-primary" />
        </div>
        <div className="text-center px-6">
          <p className="text-md font-medium text-foreground">
            Click to upload room photo
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PNG, JPG up to 5MB
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleChange}
          className="hidden"
        />
      </button>

      {/* Source Selection Dialog */}
      <SourceSelectionDialog
        open={showSourceDialog}
        onOpenChange={setShowSourceDialog}
        title="Add Photo"
        onTakePhoto={handleTakePhoto}
        onPickFromGallery={handlePickFromGallery}
      />
    </>
  );
};
